/**
 * Dashboard Page
 * Requirements: 3.5
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { api } from '../services/api';
import { Header } from '../components/Header';
import { Sidebar } from '../components/Sidebar';
import { JobCard } from '../components/JobCard';
import { AddJobModal } from '../components/AddJobModal';
import { AddJobFromURL } from '../components/AddJobFromURL';
import { AddJobFromEmail } from '../components/AddJobFromEmail';
import { PreferencesModal } from '../components/PreferencesModal';
import { SubcomponentEditor } from '../components/SubcomponentEditor';
import { ResumeEditor } from '../components/ResumeEditor';
import { JobDescriptionEditor } from '../components/JobDescriptionEditor';
import type { UserJob, JobPhase, SubcomponentType, UserPreferences } from '../types';
import { VALID_PHASES, ACTIVE_PHASES } from '../types';
import { KanbanBoard } from '../components/KanbanBoard';
import { UpgradePromptModal } from '../components/UpgradePromptModal';
import { UpgradeModal, UpgradeModalReason } from '../components/UpgradeModal';
import { StripeCheckoutModal } from '../components/StripeCheckoutModal';
import { Loader2, Inbox, LayoutGrid, Columns, CheckCircle, Info, X, Menu } from 'lucide-react';
import useJobPolling from '../hooks/useJobPolling';
import { HelpToggleButton } from '../help/HelpContext';
import useCheckoutNotification from '../hooks/useCheckoutNotification';
import useAutoFinalGeneration from '../hooks/useAutoFinalGeneration';

// Resolve URL phase param (case-insensitive) to a valid phase/filter value
function resolvePhaseParam(param: string | undefined): JobPhase | 'active' | 'all' | null {
  if (!param) return null;
  const lower = param.toLowerCase();
  if (lower === 'active') return 'active';
  if (lower === 'all') return 'all';
  const match = VALID_PHASES.find(p => p.toLowerCase() === lower);
  return match || null;
}

export function Dashboard() {
  const { phase: phaseParam } = useParams<{ phase?: string }>();
  const navigate = useNavigate();
  const urlPhase = resolvePhaseParam(phaseParam);

  const [jobs, setJobs] = useState<UserJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResume, setSelectedResume] = useState<string | null>(null);
  const [selectedPhase, setSelectedPhase] = useState<JobPhase | 'active' | 'all'>(urlPhase || 'active');
  const [phaseCounts, setPhaseCounts] = useState<Record<JobPhase, number>>({} as Record<JobPhase, number>);
  const [searchQuery, setSearchQuery] = useState('');
  const [userPreferences, setUserPreferences] = useState<UserPreferences | undefined>(undefined);

  // Modals
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [showAddJobUrlModal, setShowAddJobUrlModal] = useState(false);
  const [showAddJobEmailModal, setShowAddJobEmailModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [showResumeEditor, setShowResumeEditor] = useState(false);
  const [editingResumeName, setEditingResumeName] = useState<string | undefined>(undefined);
  const [editingSubcomponent, setEditingSubcomponent] = useState<{
    jobid: string;
    component: SubcomponentType;
    content: string;
  } | null>(null);
  const [editingJob, setEditingJob] = useState<{
    jobid: string;
    jobdesc: string;
    jobtags: string[];
    super_tags?: string[];
    jobtitle: string;
    jobcompany: string;
    joblocation: string;
    jobsalary: string;
    jobreqid: string;
    tagsGenerating?: boolean;
  } | null>(null);
  // View mode: grid (default) or kanban
  const [viewMode, setViewMode] = useState<'grid' | 'kanban'>('grid');

  // Mobile sidebar state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Upgrade prompt modal
  const [upgradeModal, setUpgradeModal] = useState<{open: boolean, type: 'resume_soft'|'resume_hard'|'job'|'ai'}>({open: false, type: 'resume_soft'});

  // Stripe checkout modal (triggered by Header "Upgrade Now" or ?upgrade=true URL param)
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);

  // Free-gen upgrade modal (triggered by JobCard hover overlay, exhausted points, etc.)
  const [freeGenUpgradeModal, setFreeGenUpgradeModal] = useState<{ open: boolean; reason: UpgradeModalReason }>({ open: false, reason: 'general' });

  const handleUpgradeRequest = useCallback((reason: UpgradeModalReason) => {
    setFreeGenUpgradeModal({ open: true, reason });
  }, []);

  const handleFreeGenConsumed = useCallback(() => {
    setUserPreferences((prev) => {
      if (!prev || prev.free_gen_remaining === undefined) return prev;
      return { ...prev, free_gen_remaining: Math.max(0, prev.free_gen_remaining - 1) };
    });
  }, []);

  // Checkout notification hook
  const { checkoutNotification, setCheckoutNotification } = useCheckoutNotification({ setUserPreferences });

  // Auto-open Stripe checkout from ?upgrade=true URL param (e.g., from pricing page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'true') {
      setShowStripeCheckout(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Resume refresh key: bump to trigger Header resume list reload without full page refresh
  const [resumeRefreshKey, setResumeRefreshKey] = useState(0);

  // Track whether initial phase selection has been applied
  const initialPhaseApplied = useRef(false);

  // Pending optimistic phase changes — jobid → {target phase, expiry timestamp}.
  // Needed because useJobPolling fires loadJobs(true) every 2s when any job is
  // Generating, and the server write for a phase change can take ~10s. Without
  // this overlay, each poll overwrites the optimistic phase with the still-old
  // server phase, causing the card to snap back repeatedly. Entries are cleared
  // when the server catches up (server phase == pending phase) or after 30s
  // (safety TTL so a truly-failed write doesn't leave the card stuck).
  const pendingPhaseChanges = useRef<Map<string, { phase: JobPhase; expires: number }>>(new Map());
  const PENDING_PHASE_TTL_MS = 30_000;

  const loadJobs = useCallback(async (isBackgroundRefresh = false) => {
    // Only show loading spinner on initial load or explicit refresh, not during background polling
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);
    try {
      let response;
      // Kanban mode always loads all jobs (needs every phase for columns)
      if (viewMode === 'kanban') {
        response = await api.listJobs(undefined, 'all');
      } else if (selectedPhase === 'active') {
        response = await api.listJobs(undefined, 'active');
      } else if (selectedPhase === 'all') {
        response = await api.listJobs(undefined, 'all');
      } else {
        response = await api.listJobs(selectedPhase);
      }

      // Reconcile server response with any in-flight optimistic phase changes
      const serverJobs: UserJob[] = response.jobs || [];
      const serverPhaseCounts: Record<JobPhase, number> = { ...(response.phaseCounts || {}) };
      const now = Date.now();
      const mergedJobs = serverJobs.map((j) => {
        const pending = pendingPhaseChanges.current.get(j.jobid);
        if (!pending) return j;
        // Server caught up, OR the overlay expired — drop the pending entry.
        if (j.jobphase === pending.phase || now > pending.expires) {
          pendingPhaseChanges.current.delete(j.jobid);
          return j;
        }
        // Still pending — keep the optimistic phase + adjust phaseCounts to match.
        const serverPhase = j.jobphase;
        if (serverPhase !== pending.phase) {
          serverPhaseCounts[serverPhase] = Math.max(0, (serverPhaseCounts[serverPhase] || 0) - 1);
          serverPhaseCounts[pending.phase] = (serverPhaseCounts[pending.phase] || 0) + 1;
        }
        return { ...j, jobphase: pending.phase };
      });
      // The list endpoint now does server-side phase filtering, so a job that
      // optimistically moved to a *different* phase than the current view
      // simply won't appear in serverJobs. If we don't sweep here, the
      // pending entry sticks around until its 30s TTL and the next time the
      // user visits the OLD tab the job won't be filtered out by the overlay
      // (but it also won't appear in the response since it's moved), so it's
      // harmless — yet stale entries accumulate. Clear them once the job is
      // no longer visible in the current view.
      const serverIds = new Set(serverJobs.map((j) => j.jobid));
      for (const [jobid, p] of pendingPhaseChanges.current.entries()) {
        if (!serverIds.has(jobid) && p.phase !== selectedPhase) {
          pendingPhaseChanges.current.delete(jobid);
        }
      }
      setJobs(mergedJobs);
      setPhaseCounts(serverPhaseCounts);
    } catch (err: any) {
      console.error('Failed to load jobs:', err);
      setError(err?.message || 'Failed to load jobs');
      setJobs([]);
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, [selectedPhase, viewMode]);

  // Sync URL with selected phase
  useEffect(() => {
    const urlPath = selectedPhase === 'active' ? '/' : `/${selectedPhase}`;
    const currentPath = window.location.pathname;
    if (currentPath !== urlPath) {
      navigate(urlPath, { replace: true });
    }
  }, [selectedPhase, navigate]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Request 3: Dynamic default phase — after initial load, pick Queued if any exist, else Ready
  // Skip if URL already specified a phase
  useEffect(() => {
    if (urlPhase) { initialPhaseApplied.current = true; return; }
    if (initialPhaseApplied.current) return;
    if (loading) return; // Wait until first load completes
    if (Object.keys(phaseCounts).length === 0 && jobs.length === 0) return; // No data yet

    initialPhaseApplied.current = true;
    if ((phaseCounts['Queued'] || 0) > 0) {
      setSelectedPhase('Queued');
    } else {
      setSelectedPhase('Ready');
    }
  }, [loading, phaseCounts, jobs.length]);

  // Load user preferences on mount
  useEffect(() => {
    async function loadPreferences() {
      try {
        const prefs = await api.getPreferences();
        setUserPreferences(prefs);
      } catch (err: any) {
        console.error('Failed to load user preferences:', err);
        const status = err?.response?.status;
        // 403 on preferences means user exists in Cognito but not in backend DB.
        // Clear auth state and redirect to login.
        if (status === 403) {
          console.warn('User not found in backend (403). Clearing auth state.');
          try {
            await signOut();
          } catch {
            localStorage.clear();
            sessionStorage.clear();
          }
          window.location.href = '/';
        }
      }
    }
    loadPreferences();
  }, []);

  // Merge a single freshly-polled job into local state. Honors the
  // pendingPhaseChanges overlay (so user-initiated Move To wins over a
  // concurrent server-side phase transition) and delta-updates phaseCounts
  // on phase change so the sidebar stays in sync without a full refetch.
  const handlePollingUpdate = useCallback((updatedJob: UserJob) => {
    const old = jobs.find((j) => j.jobid === updatedJob.jobid);
    if (!old) return;

    const pending = pendingPhaseChanges.current.get(updatedJob.jobid);
    const effectivePhase = pending && Date.now() < pending.expires
      ? pending.phase
      : updatedJob.jobphase;

    setJobs((prev) => prev.map((j) =>
      j.jobid === updatedJob.jobid
        ? { ...j, ...updatedJob, jobphase: effectivePhase }
        : j
    ));

    if (old.jobphase !== effectivePhase) {
      const oldPhase = old.jobphase;
      setPhaseCounts((prev) => ({
        ...prev,
        [oldPhase]: Math.max(0, (prev[oldPhase] || 0) - 1),
        [effectivePhase]: (prev[effectivePhase] || 0) + 1,
      }));
    }
  }, [jobs]);

  // Job polling (generation tracking, per-job active polling, 30s full refresh)
  useJobPolling({ jobs, loadJobs, onPollingUpdate: handlePollingUpdate });

  // Filter jobs based on search query
  const filteredJobs = useMemo(() => {
    // Client-side phase filter so optimistic phase changes visually remove the
    // card from the current tab the instant the user clicks Move To, rather
    // than waiting for the next background poll (up to 30s) to re-fetch a
    // phase-filtered list from the server. Kanban view shows every phase in
    // its own column, so it never filters here.
    let result = jobs;
    if (viewMode !== 'kanban') {
      if (selectedPhase === 'active') {
        result = result.filter(job => ACTIVE_PHASES.includes(job.jobphase));
      } else if (selectedPhase !== 'all') {
        result = result.filter(job => job.jobphase === selectedPhase);
      }
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((job) =>
        job.jobtitle.toLowerCase().includes(query) ||
        job.jobcompany.toLowerCase().includes(query) ||
        (job.joblocation && job.joblocation.toLowerCase().includes(query)) ||
        (job.jobtags && job.jobtags.some(tag => tag.toLowerCase().includes(query)))
      );
    }
    return result;
  }, [jobs, searchQuery, selectedPhase, viewMode]);

  // Auto-final-generation hook
  const { handleGenerateResume } = useAutoFinalGeneration({ jobs, loadJobs });

  // Optimistic job update: replace a single job in the jobs array without re-fetching
  const handleJobUpdate = useCallback((updatedJob: UserJob) => {
    setJobs(prev => prev.map(j =>
      j.jobid === updatedJob.jobid ? updatedJob : j
    ));
  }, []);

  // Optimistic phase change for kanban drag-and-drop + JobCard Skip / Move To / Generate.
  // Also records a pending entry so background polling (useJobPolling, 2s interval
  // during generation) doesn't overwrite this with stale server state before the
  // write lands. See pendingPhaseChanges comment for details.
  const handleOptimisticPhaseChange = useCallback((jobid: string, newPhase: JobPhase) => {
    pendingPhaseChanges.current.set(jobid, {
      phase: newPhase,
      expires: Date.now() + PENDING_PHASE_TTL_MS,
    });
    setJobs(prev => prev.map(j =>
      j.jobid === jobid ? { ...j, jobphase: newPhase } : j
    ));
    setPhaseCounts(prev => {
      const oldPhase = jobs.find(j => j.jobid === jobid)?.jobphase;
      if (!oldPhase || oldPhase === newPhase) return prev;
      return {
        ...prev,
        [oldPhase]: Math.max(0, (prev[oldPhase] || 0) - 1),
        [newPhase]: (prev[newPhase] || 0) + 1,
      };
    });
  }, [jobs]);

  async function handleEditSubcomponent(jobid: string, component: SubcomponentType) {
    // The /jobs list response slims data* fields to truthy markers ('1') to
    // stay under Lambda's 6MB response limit, so cached content here is the
    // marker — fetch the full job to get the real subcomponent text.
    try {
      const fullJob = await api.getJob(jobid);
      const content = fullJob[`data${component}` as keyof UserJob] as string || '';
      setEditingSubcomponent({ jobid, component, content });
    } catch (err) {
      console.error('Failed to load subcomponent content:', err);
      setEditingSubcomponent({ jobid, component, content: '' });
    }
  }

  function handleEditJob(jobid: string) {
    const job = jobs.find((j) => j.jobid === jobid);
    if (job) {
      const currentTags = job.jobtags || [];
      const baseEditing = {
        jobid,
        jobdesc: job.jobdesc,
        jobtags: currentTags,
        super_tags: job.super_tags || [],
        jobtitle: job.jobtitle || '',
        jobcompany: job.jobcompany || '',
        joblocation: job.joblocation || '',
        jobsalary: job.jobsalary || '',
        jobreqid: job.jobreqid || '',
      };
      setEditingJob(baseEditing);

      // Auto-regenerate tags if the job has fewer than 5
      if (currentTags.length < 5 && job.jobdesc) {
        setEditingJob({ ...baseEditing, tagsGenerating: true });

        // Trigger tag generation by re-saving the job description (backend fires async gen_tags Lambda)
        api.updateJobDescription(jobid, job.jobdesc).catch((err) => {
          console.warn('Background tag generation trigger failed:', err);
        });

        // Poll for updated tags (gen_tags Lambda takes ~3-5s)
        let attempts = 0;
        const maxAttempts = 5;
        const pollInterval = 3000;
        const tagPollTimer = setInterval(async () => {
          attempts++;
          try {
            const refreshedJob = await api.getJob(jobid);
            const newTags = refreshedJob.jobtags || [];
            if (newTags.length > currentTags.length || attempts >= maxAttempts) {
              clearInterval(tagPollTimer);
              if (newTags.length > currentTags.length) {
                // Update editingJob state so JobDescriptionEditor picks up new tags
                setEditingJob((prev) =>
                  prev && prev.jobid === jobid
                    ? { ...prev, jobtags: newTags, tagsGenerating: false }
                    : prev
                );
                // Also update the job in the main jobs list
                handleJobUpdate(refreshedJob);
              } else {
                // Max attempts reached without new tags — stop the indicator
                setEditingJob((prev) =>
                  prev && prev.jobid === jobid
                    ? { ...prev, tagsGenerating: false }
                    : prev
                );
              }
            }
          } catch (err) {
            console.warn('Tag poll failed:', err);
            clearInterval(tagPollTimer);
            setEditingJob((prev) =>
              prev && prev.jobid === jobid
                ? { ...prev, tagsGenerating: false }
                : prev
            );
          }
        }, pollInterval);
      }
    }
  }

  function handleRegenerateTags() {
    if (!editingJob) return;
    const { jobid, jobdesc } = editingJob;

    // Snapshot the existing tags so we can (a) detect when regeneration has
    // actually produced a NEW set and (b) restore them if it fails/times out.
    // We do NOT persist an empty list to the backend — gen_tags REPLACES
    // jobtags on success, so the old tags survive until new ones land. This
    // prevents the "regenerate → zero tags" data loss when generation is
    // skipped or errors. The clear below is UI-only (visual "regenerating").
    const oldTags = editingJob.jobtags || [];
    const oldTagsJson = JSON.stringify(oldTags);
    setEditingJob(prev => prev ? { ...prev, jobtags: [], tagsGenerating: true } : prev);

    // Trigger regeneration via a job-description re-save (force=True on the
    // backend bypasses the enabled-cost-guard for this explicit user action).
    api.updateJobDescription(jobid, jobdesc).catch(err => {
      console.warn('Regenerate tags trigger failed:', err);
    });

    // Poll until the tag set CHANGES from the snapshot (new tags arrived) or we
    // time out. On timeout we restore the snapshot rather than show zero.
    let attempts = 0;
    const maxAttempts = 20; // 20 x 3s = 60s
    const pollInterval = 3000;
    const tagPollTimer = setInterval(async () => {
      attempts++;
      try {
        const refreshedJob = await api.getJob(jobid);
        const newTags = refreshedJob.jobtags || [];
        const changed = JSON.stringify(newTags) !== oldTagsJson && newTags.length > 0;
        if (changed || attempts >= maxAttempts) {
          clearInterval(tagPollTimer);
          // Prefer the freshly generated set; if none arrived, keep the old tags.
          const finalTags = changed ? newTags : oldTags;
          setEditingJob(prev =>
            prev && prev.jobid === jobid
              ? {
                  ...prev,
                  jobtags: finalTags,
                  // Refresh tag categories too — gen_tags regenerates super_tags
                  // alongside jobtags, so the Tag Categories section must update.
                  ...(changed ? { super_tags: refreshedJob.super_tags || [] } : {}),
                  tagsGenerating: false,
                }
              : prev
          );
          if (changed) {
            handleJobUpdate(refreshedJob);
          }
        }
      } catch (err) {
        console.warn('Tag regen poll failed:', err);
        clearInterval(tagPollTimer);
        // Restore the snapshot on poll failure — never leave the user at zero.
        setEditingJob(prev =>
          prev && prev.jobid === jobid
            ? { ...prev, jobtags: oldTags, tagsGenerating: false }
            : prev
        );
      }
    }, pollInterval);
  }

  async function handleSaveSubcomponent(content: string) {
    if (!editingSubcomponent) return;
    try {
      await api.updateSubcomponentContent(
        editingSubcomponent.jobid,
        editingSubcomponent.component,
        content
      );
      await loadJobs();
    } catch (error) {
      console.error('Failed to save subcomponent:', error);
      throw error; // Re-throw so SubcomponentEditor can handle it
    }
  }

  async function handleNewResume() {
    // Check resume count against limits
    try {
      const response = await api.listResumes();
      const resumeCount = response.resumes?.length || 0;
      const status = userPreferences?.status;
      const allowedResumes = userPreferences?.allowed_master_resumes;
      const softCap = 5;

      // Hard cap for trial/expired users (default 1 for trial, 1 for expired)
      if (status === 'trial' || status === 'expired') {
        const hardLimit = allowedResumes ?? 1;
        if (resumeCount >= hardLimit) {
          setUpgradeModal({ open: true, type: 'resume_hard' });
          return;
        }
      }

      // Soft cap for active users
      if (status === 'active' || status === 'hibernating') {
        const limit = allowedResumes ?? softCap;
        if (resumeCount >= limit) {
          setUpgradeModal({ open: true, type: 'resume_soft' });
          return;
        }
      }
    } catch {
      // If we can't check, allow creation
    }

    setEditingResumeName(undefined);
    setShowResumeEditor(true);
  }

  function handleEditResume(resumename: string) {
    setEditingResumeName(resumename);
    setShowResumeEditor(true);
  }

  async function handleDeleteResume(resumename: string) {
    await api.deleteResume(resumename);
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Skip to main content link (WCAG 2.4.1) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>

      {/* Checkout result notification banner */}
      {checkoutNotification && (
        <div className={`flex items-center justify-between px-4 py-3 ${
          checkoutNotification.type === 'success'
            ? 'bg-green-50 text-green-800 border-b border-green-200'
            : 'bg-yellow-50 text-yellow-800 border-b border-yellow-200'
        }`}>
          <div className="flex items-center">
            {checkoutNotification.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            ) : (
              <Info className="w-5 h-5 mr-2 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{checkoutNotification.message}</span>
          </div>
          <button
            onClick={() => setCheckoutNotification(null)}
            className="p-1 hover:bg-black/5 rounded"
            aria-label="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <Header
        onAddJobUrl={() => setShowAddJobUrlModal(true)}
        onAddJobManual={() => setShowAddJobModal(true)}
        onAddJobEmail={() => setShowAddJobEmailModal(true)}
        onOpenSettings={() => setShowPreferencesModal(true)}
        onUpgrade={() => setShowStripeCheckout(true)}
        selectedResume={selectedResume}
        onSelectResume={setSelectedResume}
        onEditResume={handleEditResume}
        onNewResume={handleNewResume}
        userPreferences={userPreferences}
        onPreferencesChange={(prefs) => setUserPreferences(prev => prev ? { ...prev, ...prefs } : prev)}
        resumeRefreshKey={resumeRefreshKey}
      />

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - desktop: always visible; mobile: slide-in drawer */}
        {viewMode === 'grid' && (
          <>
            {/* Desktop sidebar (hidden on mobile) */}
            <div className="hidden md:block">
              <Sidebar
                selectedPhase={selectedPhase}
                onSelectPhase={setSelectedPhase}
                phaseCounts={phaseCounts}
                onAddJobUrl={() => setShowAddJobUrlModal(true)}
                onAddJobManual={() => setShowAddJobModal(true)}
                onAddJobEmail={() => setShowAddJobEmailModal(true)}
                userPreferences={userPreferences}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </div>

            {/* Mobile sidebar overlay */}
            {mobileSidebarOpen && (
              <div
                className="md:hidden fixed inset-0 bg-black/40 z-40"
                onClick={() => setMobileSidebarOpen(false)}
              />
            )}
            <div
              className={`md:hidden fixed top-0 left-0 h-full w-64 bg-white z-50 transform transition-transform duration-200 ease-in-out ${
                mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
            >
              <Sidebar
                selectedPhase={selectedPhase}
                onSelectPhase={(phase) => {
                  setSelectedPhase(phase);
                  setMobileSidebarOpen(false);
                }}
                phaseCounts={phaseCounts}
                onAddJobUrl={() => { setShowAddJobUrlModal(true); setMobileSidebarOpen(false); }}
                onAddJobManual={() => { setShowAddJobModal(true); setMobileSidebarOpen(false); }}
                onAddJobEmail={() => { setShowAddJobEmailModal(true); setMobileSidebarOpen(false); }}
                userPreferences={userPreferences}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </div>
          </>
        )}

        <main id="main-content" className={`flex-1 overflow-y-auto ${viewMode === 'kanban' ? 'p-3 overflow-x-auto' : 'p-6'}`}>
          {/* View mode toggle + mobile sidebar button */}
          <div className="flex items-center justify-between mb-3">
            {/* Mobile sidebar toggle (only in grid mode) */}
            {viewMode === 'grid' ? (
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="md:hidden p-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                title="Show filters"
                aria-label="Show filters"
              >
                <Menu className="w-5 h-5" />
              </button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-1 ml-auto">
            <button
              onClick={() => setViewMode('grid')}
              data-help-target="mainapp.viewmain"
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Grid view"
              aria-label="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              data-help-target="mainapp.viewkanban"
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title="Kanban board"
              aria-label="Kanban board"
            >
              <Columns className="w-4 h-4" />
            </button>
            <HelpToggleButton />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64" role="status" aria-label="Loading jobs">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-red-500">
              <p className="text-lg">Error loading jobs</p>
              <p className="text-sm">{error}</p>
              <button
                onClick={() => loadJobs()}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          ) : viewMode === 'kanban' ? (
            <KanbanBoard
              jobs={jobs}
              phaseCounts={phaseCounts}
              onRefresh={loadJobs}
              onEditSubcomponent={handleEditSubcomponent}
              onEditJob={handleEditJob}
              onGenerateResume={handleGenerateResume}
              onOptimisticPhaseChange={handleOptimisticPhaseChange}
              onJobUpdate={handleJobUpdate}
              selectedResume={selectedResume}
              autoDownloadFiles={userPreferences?.auto_download_files !== false}
              userPreferences={userPreferences}
              onUpgradeRequest={handleUpgradeRequest}
              onFreeGenConsumed={handleFreeGenConsumed}
            />
          ) : filteredJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Inbox className="w-12 h-12 mb-4" />
              <p className="text-lg">{searchQuery ? 'No matching jobs' : 'No jobs found'}</p>
              <p className="text-sm">{searchQuery ? 'Try a different search term' : 'Add a job to get started'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
              {filteredJobs.map((job) => (
                <JobCard
                  key={job.jobid}
                  job={job}
                  onRefresh={loadJobs}
                  onEditSubcomponent={handleEditSubcomponent}
                  onEditJob={handleEditJob}
                  onGenerateResume={handleGenerateResume}
                  onJobUpdate={handleJobUpdate}
                  onOptimisticPhaseChange={handleOptimisticPhaseChange}
                  selectedResume={selectedResume}
                  autoDownloadFiles={userPreferences?.auto_download_files !== false}
                  userPreferences={userPreferences}
                  onUpgradeRequest={handleUpgradeRequest}
                  onFreeGenConsumed={handleFreeGenConsumed}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Modals */}
      <AddJobModal
        isOpen={showAddJobModal}
        onClose={() => setShowAddJobModal(false)}
        onSuccess={loadJobs}
        selectedResume={selectedResume || ''}
      />

      {showAddJobUrlModal && (
        <AddJobFromURL
          resumeid={selectedResume || ''}
          onClose={() => setShowAddJobUrlModal(false)}
          onSuccess={loadJobs}
        />
      )}

      {showAddJobEmailModal && (
        <AddJobFromEmail
          onClose={() => setShowAddJobEmailModal(false)}
        />
      )}

      <PreferencesModal
        isOpen={showPreferencesModal}
        onClose={() => setShowPreferencesModal(false)}
      />

      <ResumeEditor
        isOpen={showResumeEditor}
        onClose={() => setShowResumeEditor(false)}
        onSuccess={(savedName) => {
          // Do NOT close here — closing is controlled by the editor's
          // onClose (Save and Close / Cancel / X). Closing here would defeat
          // the "Save" (keep-open) button.
          // Transition a newly created resume into edit-mode so further saves
          // update instead of creating duplicates, and so the editor unlocks
          // edit-only features + reloads to show generated tags.
          if (savedName) setEditingResumeName(savedName);
          // Refresh resume list in Header and reload job data (no full page reload)
          setResumeRefreshKey(prev => prev + 1);
          loadJobs(true);
        }}
        resumename={editingResumeName}
        onDeleteResume={handleDeleteResume}
      />

      {editingSubcomponent && (
        <SubcomponentEditor
          isOpen={true}
          onClose={() => setEditingSubcomponent(null)}
          onSave={handleSaveSubcomponent}
          jobid={editingSubcomponent.jobid}
          component={editingSubcomponent.component}
          initialContent={editingSubcomponent.content}
        />
      )}

      {editingJob && (
        <JobDescriptionEditor
          jobid={editingJob.jobid}
          initialContent={editingJob.jobdesc}
          initialTags={editingJob.jobtags}
          initialSuperTags={editingJob.super_tags}
          initialTitle={editingJob.jobtitle}
          initialCompany={editingJob.jobcompany}
          initialLocation={editingJob.joblocation}
          initialSalary={editingJob.jobsalary}
          initialReqid={editingJob.jobreqid}
          tagsGenerating={editingJob.tagsGenerating}
          onClose={() => setEditingJob(null)}
          onSave={loadJobs}
          onRegenerateTags={handleRegenerateTags}
        />
      )}

      <UpgradePromptModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal(prev => ({ ...prev, open: false }))}
        limitType={upgradeModal.type}
        onNavigateToSettings={() => {
          setUpgradeModal(prev => ({ ...prev, open: false }));
          setShowPreferencesModal(true);
        }}
        onProceed={() => {
          setEditingResumeName(undefined);
          setShowResumeEditor(true);
        }}
      />

      <StripeCheckoutModal
        isOpen={showStripeCheckout}
        onClose={() => setShowStripeCheckout(false)}
        onSuccess={() => {
          setShowStripeCheckout(false);
          // Refresh preferences to reflect new subscription status
          api.getPreferences().then((prefs) => {
            setUserPreferences(prefs);
          }).catch((err) => {
            console.error('Failed to refresh preferences after checkout:', err);
          });
        }}
      />

      <UpgradeModal
        isOpen={freeGenUpgradeModal.open}
        onClose={() => setFreeGenUpgradeModal({ open: false, reason: 'general' })}
        onUpgrade={() => setShowStripeCheckout(true)}
        reason={freeGenUpgradeModal.reason}
        freeGenRemaining={userPreferences?.free_gen_remaining}
      />
    </div>
  );
}
